import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tag,
  Typography,
  Descriptions,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  DollarOutlined,
  ReloadOutlined,
  EyeOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchRefunds,
  fetchRefundDetail,
  createRefund,
} from '../store/slices/refundSlice';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const RefundsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [filters, setFilters] = useState<any>({});

  const {
    refunds,
    currentRefund,
    creating,
    error,
  } = useAppSelector((state) => state.refund);

  useEffect(() => {
    dispatch(fetchRefunds({ page: 1, limit: 20 }));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleCreateRefund = async (values: any) => {
    try {
      const amount = Math.round(parseFloat(values.amount) * 100);
      await dispatch(
        createRefund({
          payment_id: values.payment_id,
          amount,
          reason: values.reason,
          description: values.description,
        })
      ).unwrap();
      message.success('退款已创建');
      setRefundModalVisible(false);
      form.resetFields();
      dispatch(fetchRefunds({ page: 1, limit: 20 }));
    } catch (err: any) {
      message.error(err || '创建退款失败');
    }
  };

  const handleViewDetail = async (id: string) => {
    try {
      await dispatch(fetchRefundDetail(id)).unwrap();
      setDetailModalVisible(true);
    } catch (err: any) {
      message.error(err || '获取退款详情失败');
    }
  };

  const handleRefresh = () => {
    dispatch(fetchRefunds({ page: 1, limit: 20, filters }));
  };

  const handleTableChange = (pagination: any, tableFilters: any) => {
    const newFilters = {
      status: tableFilters.status?.[0],
    };
    setFilters(newFilters);
    dispatch(
      fetchRefunds({
        page: pagination.current,
        limit: pagination.pageSize,
        filters: newFilters,
      })
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: '退款ID',
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {text}
        </Text>
      ),
    },
    {
      title: '支付ID',
      dataIndex: 'payment_id',
      key: 'payment_id',
      render: (text: string) => (
        <Text
          copyable
          type="secondary"
          style={{ fontFamily: 'monospace', fontSize: 12 }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: '退款金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <Text strong>
          {record.currency} {(amount / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '原始金额',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      render: (amount: number, record: any) => (
        <Text type="secondary">
          {record.currency} {((amount || 0) / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: '待处理', value: 'pending' },
        { text: '处理中', value: 'processing' },
        { text: '成功', value: 'succeeded' },
        { text: '失败', value: 'failed' },
        { text: '已取消', value: 'cancelled' },
      ],
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          pending: { label: '待处理', color: 'default' },
          processing: { label: '处理中', color: 'processing' },
          succeeded: { label: '成功', color: 'success' },
          failed: { label: '失败', color: 'error' },
          cancelled: { label: '已取消', color: 'default' },
        };
        const config = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason: string) => {
        const reasonMap: Record<string, string> = {
          duplicate: '重复支付',
          fraudulent: '欺诈',
          requested_by_customer: '客户请求',
          other: '其他',
        };
        return reasonMap[reason] || reason;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '完成时间',
      dataIndex: 'completed_at',
      key: 'completed_at',
      render: (text: string) =>
        text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.id)}
        >
          查看详情
        </Button>
      ),
    },
  ];

  const totalRefunded =
    refunds.data?.reduce((sum, r) => sum + (r.status === 'succeeded' ? r.amount : 0), 0) ||
    0;
  const totalPending =
    refunds.data?.reduce(
      (sum, r) => sum + (r.status === 'pending' || r.status === 'processing' ? r.amount : 0),
      0
    ) || 0;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          退款管理
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<RollbackOutlined />}
            onClick={() => setRefundModalVisible(true)}
          >
            创建退款
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总退款数"
              value={refunds.meta?.total || 0}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="已退款金额"
              value={totalRefunded / 100}
              precision={2}
              suffix="USD"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="待处理金额"
              value={totalPending / 100}
              precision={2}
              suffix="USD"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={refunds.data || []}
          loading={refunds.loading}
          rowKey="id"
          locale={{ emptyText: '暂无数据' }}
          onChange={handleTableChange}
          pagination={{
            total: refunds.meta?.total || 0,
            current: refunds.meta?.current_page || 1,
            pageSize: refunds.meta?.per_page || 20,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
        />
      </Card>

      <Modal
        title="创建退款"
        open={refundModalVisible}
        onCancel={() => {
          setRefundModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateRefund}>
          <Form.Item
            label="支付ID"
            name="payment_id"
            rules={[{ required: true, message: '请输入支付ID' }]}
          >
            <Input placeholder="请输入支付ID" />
          </Form.Item>

          <Form.Item
            label="退款金额"
            name="amount"
            rules={[
              { required: true, message: '请输入退款金额' },
              {
                validator: (_, value) => {
                  const amount = parseFloat(value);
                  if (isNaN(amount) || amount <= 0) {
                    return Promise.reject('请输入有效的金额');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              prefix="USD"
              placeholder="请输入金额"
              type="number"
              step="0.01"
            />
          </Form.Item>

          <Form.Item
            label="退款原因"
            name="reason"
            rules={[{ required: true, message: '请选择退款原因' }]}
          >
            <Select placeholder="请选择">
              <Select.Option value="duplicate">重复支付</Select.Option>
              <Select.Option value="fraudulent">欺诈</Select.Option>
              <Select.Option value="requested_by_customer">客户请求</Select.Option>
              <Select.Option value="other">其他</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="描述" name="description">
            <Input.TextArea rows={4} placeholder="请输入退款描述" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setRefundModalVisible(false);
                  form.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                创建退款
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="退款详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        {currentRefund && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="退款ID" span={2}>
              <Text copyable>{currentRefund.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="支付ID" span={2}>
              <Text copyable>{currentRefund.payment_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="退款金额">
              <Text strong>
                {currentRefund.currency} {(currentRefund.amount / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="原始金额">
              <Text type="secondary">
                {currentRefund.currency}{' '}
                {((currentRefund.payment_amount || 0) / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag
                color={
                  currentRefund.status === 'succeeded'
                    ? 'success'
                    : currentRefund.status === 'failed'
                    ? 'error'
                    : 'processing'
                }
              >
                {currentRefund.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="原因">
              {currentRefund.reason}
            </Descriptions.Item>
            <Descriptions.Item label="描述" span={2}>
              {currentRefund.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(currentRefund.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="完成时间">
              {currentRefund.completed_at
                ? dayjs(currentRefund.completed_at).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            {currentRefund.failure_reason && (
              <Descriptions.Item label="失败原因" span={2}>
                <Text type="danger">{currentRefund.failure_reason}</Text>
              </Descriptions.Item>
            )}
            {currentRefund.metadata && Object.keys(currentRefund.metadata).length > 0 && (
              <Descriptions.Item label="元数据" span={2}>
                <pre style={{ margin: 0 }}>
                  {JSON.stringify(currentRefund.metadata, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default RefundsPage;
